import { useMemo, type ReactNode } from "react";
import type { SxProps, Theme } from "@mui/material/styles";
import { Box, Typography, List, ListItem, CircularProgress, Button, Stack } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useGetInvoicesByJobEvent } from "../hooks/useGetInvoicesByJobEvent";
import { useGetInvoice } from "./ClientView/hooks/useGetInvoice";
import { InvoiceDetailsView } from "./DetailsView";
import { useModals } from "../../../../context/ModalContext";
import type { IInvoice } from "../../create/interfaces/invoice.interfaces";
import { InvoiceCreateView } from "../../create/InvoiceCreateView";
import { BaseModalComponent } from "../../../common/BaseModalComponent";
import { useCancelInvoice } from "../hooks/useCancelInvoice";
import { useDialog } from "../../../ui/dialogs/useDialog";
import { useWatchInvoiceNotifications } from "../../../../notifications/hooks/useWatchInvoiceNotifications";
import { useQueryClient } from "@tanstack/react-query";
import type { JobEventForInvoice } from "../../../jobs/jobs-details/events/board/context";
import { InvoiceListItem } from "./shared/InvoiceListItem";
import { canIssueNewInvoice } from "./shared/invoiceLifecycle";

interface InvoiceListModalContentProps {
  jobEventUid: string;
  invoiceJobEvent: JobEventForInvoice;
  scopeOfWorkUid: string;
  invoicePreviewModalKey: string;
  /** Modal stack key for the visit invoice list (close/reopen list from details). */
  invoiceListModalKey: string;
  createModalKey: string;
  startDateLabel: string;
  jobUid: number;
  clientUid: string;
  eventSchedulerUid: string;
  /** From JobEvents row; blocks "Create" if list endpoint errors or omits paid invoices */
  billingInvoiceUid?: string | null;
}

const EmptyStateMessage = ({ children, error, sx }: { children: ReactNode; error?: boolean; sx?: SxProps<Theme> }) => (
  <Typography variant="body2" color={error ? "error" : "text.secondary"} sx={{ mb: 2, maxWidth: 520, lineHeight: 1.6, ...sx }}>
    {children}
  </Typography>
);

export function InvoiceListModalContent({
  jobEventUid,
  invoiceJobEvent,
  scopeOfWorkUid,
  invoicePreviewModalKey,
  invoiceListModalKey,
  createModalKey,
  startDateLabel,
  jobUid,
  clientUid,
  eventSchedulerUid,
  billingInvoiceUid,
}: InvoiceListModalContentProps) {
  const { data: invoices, isLoading: isLoadingList, isError: isListError, refetch: refetchList } = useGetInvoicesByJobEvent(jobEventUid);
  const billingUid = billingInvoiceUid ?? "";
  const { data: linkedInvoice, isLoading: isLoadingLinked, isError: isLinkedFetchError, refetch: refetchLinked } = useGetInvoice(billingUid);

  const mergedInvoices = useMemo(() => {
    const list = invoices ?? [];
    if (linkedInvoice && !list.some((i) => i.uid === linkedInvoice.uid)) {
      return [linkedInvoice, ...list];
    }
    if (list.length) return list;
    return linkedInvoice ? [linkedInvoice] : [];
  }, [invoices, linkedInvoice]);

  const queryClient = useQueryClient();
  useWatchInvoiceNotifications({
    onNotificationArrived: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices", "by-job-event", jobEventUid] });
      if (billingUid) {
        queryClient.invalidateQueries({ queryKey: ["invoice", billingUid] });
      }
    },
  });
  const { cancelInvoice } = useCancelInvoice();
  const { showDialog, DialogComponent } = useDialog();
  const { openModal } = useModals();

  const CREATE_KEY = `InvoiceCreate-${jobEventUid}`;

  // Refactor this modal to invoke in BaseModalComponent as simple as possible
  const handleSelectInvoice = (invoice: IInvoice) => {
    openModal(
      { key: invoicePreviewModalKey, preventParentTrigger: true },
      <Box sx={{ minHeight: "700px" }}>
        <InvoiceDetailsView
          invoiceUid={invoice.uid}
          scopeOfWorkUid={scopeOfWorkUid}
          invoicePreviewModalKey={invoicePreviewModalKey}
          invoiceListModalKey={invoiceListModalKey}
          jobEventUid={jobEventUid}
        />
      </Box>,
      null,
    );
  };

  const handleCreateInvoice = () => {
    openModal(
      { key: CREATE_KEY, preventParentTrigger: true },
      <InvoiceCreateView
        jobUid={jobUid}
        jobEvents={[invoiceJobEvent]}
        clientUid={clientUid}
        scopeOfWorkUid={scopeOfWorkUid}
        eventSchedulerUid={eventSchedulerUid}
        invoiceModal={CREATE_KEY}
        visitGroups={[
          {
            jobId: jobUid,
            jobLabel: `Job #${jobUid}`,
            eventUids: [invoiceJobEvent.uid].filter((uid): uid is string => Boolean(uid)),
          },
        ]}
      />,
      null,
    );
  };

  const handleDeleteInvoice = (invoiceUid: string) => {
    showDialog({
      type: "warning",
      title: "Delete Invoice",
      message:
        "Deleting this invoice will invalidate its payment. Any payments linked through PayPal or Stripe will also be invalidated. Are you sure you want to proceed with deleting this invoice?",
      confirmLabel: "Delete Invoice",
      cancelLabel: "Cancel",
      onConfirm: () => {
        cancelInvoice(invoiceUid, {
          billingJobEventUids: [jobEventUid],
          extraInvalidateJobEventUids: [jobEventUid],
          invoiceModal: invoicePreviewModalKey,
        });
      },
      onCancel: () => {},
    });
  };

  const hasLinkedInvoiceOnVisit = Boolean(billingInvoiceUid);
  const hasStaleBillingLink = Boolean(billingUid) && mergedInvoices.length === 0 && !linkedInvoice && !isLoadingLinked;
  const canCreateInvoice = canIssueNewInvoice(mergedInvoices) && !hasStaleBillingLink;

  /** When the list endpoint fails, wait for GET /invoices/:uid before showing an error. */
  const waitingForLinkedFallback = Boolean(billingUid && isListError && isLoadingLinked && !isLinkedFetchError);

  if (isLoadingList || waitingForLinkedFallback) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const refetchAll = () => {
    void refetchList();
    if (billingUid) void refetchLinked();
  };

  if (!mergedInvoices.length) {
    const listAndLinkFailed = isListError && (!billingUid || isLinkedFetchError || (billingUid && !linkedInvoice && !isLoadingLinked));

    const listShell = (body: ReactNode) => (
      <Box sx={{ py: 1 }}>
        <BaseModalComponent modalKey={CREATE_KEY} modalLabel="Create Invoice" defaultSizes={{ width: "80%", height: "85%" }} preventParentTrigger />
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, mb: 1, minHeight: 36 }}>
          {canCreateInvoice && (
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleCreateInvoice}>
              Create Invoice
            </Button>
          )}
        </Box>
        <List disablePadding>
          <ListItem
            sx={{
              flexDirection: "column",
              alignItems: "stretch",
              py: 2.5,
              px: 2,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            {body}
          </ListItem>
        </List>
        {DialogComponent}
      </Box>
    );

    if (hasLinkedInvoiceOnVisit) {
      return listShell(
        <Stack spacing={2} alignItems="flex-start">
          <EmptyStateMessage sx={{ textAlign: "left", mb: 0 }}>
            {listAndLinkFailed
              ? "This visit is linked to an invoice, but we could not load it. Try again or check your connection."
              : "This visit is linked to an invoice, but details could not be listed. Try refreshing."}
          </EmptyStateMessage>
          <Button variant="outlined" size="small" onClick={refetchAll}>
            Refresh
          </Button>
        </Stack>,
      );
    }

    if (listAndLinkFailed) {
      return listShell(
        <Stack spacing={2} alignItems="flex-start">
          <EmptyStateMessage error sx={{ textAlign: "left", mb: 0 }}>
            Could not load invoices for this visit.
          </EmptyStateMessage>
          <Button variant="outlined" size="small" onClick={() => void refetchList()}>
            Retry
          </Button>
        </Stack>,
      );
    }

    return listShell(
      <EmptyStateMessage sx={{ textAlign: "left", mb: 0 }}>No invoices yet for this visit. Create one when you are ready to bill.</EmptyStateMessage>,
    );
  }

  return (
    <Box sx={{ py: 1 }}>
      {/* Renderer de la 3ra capa — sin children, solo escucha el contexto */}
      <BaseModalComponent modalKey={CREATE_KEY} modalLabel="Create Invoice" defaultSizes={{ width: "80%", height: "85%" }} preventParentTrigger />

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, mb: 1 }}>
        {canCreateInvoice && (
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleCreateInvoice}>
            Create Invoice
          </Button>
        )}
      </Box>

      <List disablePadding>
        {mergedInvoices.map((inv) => (
          <InvoiceListItem key={inv.uid} invoice={inv} onClick={() => handleSelectInvoice(inv)} />
        ))}
      </List>
      {DialogComponent}
    </Box>
  );
}
